import type { DeliveryEstimate } from '../userOffers/DeliveryCell';

/**
 * „Ocena” rekomendowanego zlecenia na karcie pojazdu (07.10.2026, prototyp
 * p. Mariusza, zmiana 15; UWAGI.md: „Ocena z werdyktów backendu `onTime` /
 * `delivery`, nie z klasyfikacji ekranu”).
 *
 * Prototyp klasyfikował wiersze po treści uwag („mieści się / ciasny dolot
 * / po oknie”). Tu ocena to wyłącznie przepisanie werdyktów API
 * (`on-time-verdict.ts`, `delivery-verdict.ts`) na cztery grupy:
 *
 * - **nie zdąży** — „nie dowiezie” bez ratunku z ulg albo pewne „nie zdąży”
 *   (nieobecność kierowcy w kartotece); od 21.10.2026 (przełącznik
 *   `loadingWindowEnd`, C25 z paczki p. Mariusza) także przyjazd na
 *   załadunek po KOŃCU okna załadunku bez ratunku z ulg;
 * - **z zastrzeżeniem** — niepewne „nie zdąży” na początek okna załadunku,
 *   „dowiezie tylko z ulgami” albo powód przy „zdąży” (np. spóźni się na
 *   początek okna, ale dowiezie);
 * - **brak danych** — któregoś werdyktu nie ma (`null` to brak danych,
 *   nie „nie”);
 * - **mieści się** — zdąży i dowiezie, bez zastrzeżeń.
 *
 * To nie jest ocena złożona ani ranking: na górze są wykonalne (zmiana 15:
 * „wykonalne na górze”), a w grupie kolejność zostaje po dolocie, jak
 * z API. Silnik nikogo nie ukrywa.
 */

export type SuggestionGrade = 'ok' | 'caution' | 'unknown' | 'late';

export interface GradedSuggestion {
  grade: SuggestionGrade;
  label: string;
  /** Dlaczego taka ocena — powody z API, do dymka. */
  reasons: string[];
}

export const GRADE_LABELS: Record<SuggestionGrade, string> = {
  ok: 'mieści się',
  caution: 'z zastrzeżeniem',
  unknown: 'brak danych',
  late: 'nie zdąży',
};

/** Kolejność grup na liście: wykonalne na górze. */
const GRADE_ORDER: Record<SuggestionGrade, number> = { ok: 0, caution: 1, unknown: 2, late: 3 };

export interface VerdictInput {
  onTime: { willMakeIt: boolean | null; reason: string | null; certain: boolean };
  delivery?: DeliveryEstimate;
}

export function suggestionGrade(o: VerdictInput): GradedSuggestion {
  const reasons = [o.onTime.reason, o.delivery?.reason].filter((r): r is string => !!r);
  const out = (grade: SuggestionGrade): GradedSuggestion => ({ grade, label: GRADE_LABELS[grade], reasons });
  const reliefs = o.delivery?.willMakeIt === false && !!o.delivery.arrivalWithReliefsAt;
  // Po końcu okna załadunku (21.10.2026) — pole jest tylko z przełącznikiem.
  const loading = o.delivery?.loadingWindow;
  const lateLoading = loading?.late === true && !loading.arrivalWithReliefsAt;
  if (
    (o.delivery?.willMakeIt === false && !reliefs) ||
    lateLoading ||
    (o.onTime.willMakeIt === false && o.onTime.certain)
  ) {
    return out('late');
  }
  if (o.onTime.willMakeIt === false || reliefs || loading?.late === true) return out('caution');
  if (o.onTime.willMakeIt === null || !o.delivery || o.delivery.willMakeIt === null) return out('unknown');
  return out(o.onTime.reason ? 'caution' : 'ok');
}

/** Wykonalne na górze; w grupie kolejność z API (po dolocie). */
export function byGrade<T extends VerdictInput>(rows: readonly T[]): T[] {
  return rows
    .map((row, index) => ({ row, index, rank: GRADE_ORDER[suggestionGrade(row).grade] }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((x) => x.row);
}
